import { minimumPeriod } from "hooks/periodIndex";
import { useTenant } from "hooks/settings";
import { useEffect, useMemo } from "react";
import {
  CostSummary,
  MixRead,
  NameBasedSteelGradePlanItem,
  UnsummarisedProductionMixResult,
  useGetDeployedPlanResultsQuery,
  useGetHeatQueueQuery,
  useListHeatOverrideRecipesQuery,
  useSummarisePlanResultsMutation,
} from "src/store/api/generatedApi";

const STANDING_DEPLOYMENT_TYPES = ["standard", "edited_standard"] as const;

const getOverrideCostSummariesCacheKey = (tenantName: string): string =>
  `overrideCostSummaries:${tenantName}`;

// `manual` round-trips each recipe's stored baskets verbatim; the summarise
// call is only for pricing, so re-stratifying the layers would be wasted work.
const toSummariseInput = ({
  name,
  production,
  mix,
  mixId,
}: {
  name: string;
  production: NameBasedSteelGradePlanItem[];
  mix: MixRead;
  mixId: number;
}): UnsummarisedProductionMixResult => ({
  name,
  production,
  mix: {
    baskets: mix.baskets,
    furnace: mix.furnace,
    thresholds: mix.thresholds,
  },
  original_mix_id: mixId,
  layer_source: "manual",
});

// Summarises every standing recipe, override candidate, heat-queue floor
// recipe, and active override's replaced mix in one batched current-prices
// call. Per-heat and per-tonne costs depend only on the mix, not on which
// heats are selected, so one batch covers every selection. Refires when the
// standing plan, candidate list, or heat queue change.
export const useSummariseOverrideCosts = ({
  tenantName,
  enabled,
}: {
  tenantName: string;
  enabled: boolean;
}): void => {
  const standingPlan = useGetDeployedPlanResultsQuery(
    {
      tenantName,
      planId: "latest",
      period: minimumPeriod,
      deploymentTypes: [...STANDING_DEPLOYMENT_TYPES],
    },
    { skip: !enabled }
  );
  const recipes = useListHeatOverrideRecipesQuery(
    { tenantName },
    { skip: !enabled }
  );
  const heatQueue = useGetHeatQueueQuery({ tenantName }, { skip: !enabled });
  const [summarise] = useSummarisePlanResultsMutation({
    fixedCacheKey: getOverrideCostSummariesCacheKey(tenantName),
  });

  const standingPlanId = standingPlan.data?.plan_id;
  const standingMixes =
    standingPlan.data?.periods[minimumPeriod.toString()]?.mixes;
  const recipeItems = recipes.data?.items;
  const floorRecipes = useMemo(
    () =>
      (heatQueue.data?.heats ?? []).flatMap((heat) =>
        heat.floor_recipe === null ? [] : [heat.floor_recipe]
      ),
    [heatQueue.data]
  );
  const overrideMixes = useMemo(
    () =>
      (heatQueue.data?.active_overrides ?? []).flatMap((override) => [
        override.replaced_mix,
        override.override_mix,
      ]),
    [heatQueue.data]
  );
  const heatQueuePending = heatQueue.isLoading;

  useEffect(() => {
    if (
      !enabled ||
      standingPlanId === undefined ||
      standingMixes === undefined ||
      recipeItems === undefined ||
      heatQueuePending
    ) {
      return;
    }
    const inputsByMixId = new Map(
      standingMixes.map((group) => [
        group.mix.id,
        toSummariseInput({
          name: group.name,
          production: group.production,
          mix: group.mix,
          mixId: group.mix.id,
        }),
      ])
    );
    recipeItems
      .filter((recipe) => !inputsByMixId.has(recipe.mix_id))
      .forEach((recipe) => {
        inputsByMixId.set(
          recipe.mix_id,
          toSummariseInput({
            name: recipe.name,
            production: [],
            mix: recipe.mix,
            mixId: recipe.mix_id,
          })
        );
      });
    floorRecipes
      .filter((floorRecipe) => !inputsByMixId.has(floorRecipe.mix.id))
      .forEach((floorRecipe) => {
        inputsByMixId.set(
          floorRecipe.mix.id,
          toSummariseInput({
            name: floorRecipe.name,
            production: [],
            mix: floorRecipe.mix,
            mixId: floorRecipe.mix.id,
          })
        );
      });
    overrideMixes
      .filter((mix) => !inputsByMixId.has(mix.id))
      .forEach((mix) => {
        inputsByMixId.set(
          mix.id,
          toSummariseInput({
            name: `override-${mix.id}`,
            production: [],
            mix,
            mixId: mix.id,
          })
        );
      });
    const mutation = summarise({
      tenantName,
      unsummarisedPlanResults: {
        plan_id: standingPlanId,
        period: minimumPeriod,
        price_basis: "current",
        mixes: [...inputsByMixId.values()],
      },
    });
    return () => {
      mutation.abort();
    };
  }, [
    enabled,
    standingPlanId,
    standingMixes,
    recipeItems,
    floorRecipes,
    overrideMixes,
    heatQueuePending,
    summarise,
    tenantName,
  ]);
};

export const useOverrideCostSummaryMap = (): {
  costByMixId: Map<number, CostSummary>;
  isPending: boolean;
} => {
  const tenantName = useTenant();
  const [, summariseState] = useSummarisePlanResultsMutation({
    fixedCacheKey: getOverrideCostSummariesCacheKey(tenantName),
  });
  const costByMixId = useMemo(
    () =>
      new Map(
        (summariseState.data?.mixes ?? []).map((summarisedMix) => [
          summarisedMix.original_mix_id,
          summarisedMix.mix.summary.cost,
        ])
      ),
    [summariseState.data]
  );
  return {
    costByMixId,
    isPending: summariseState.isUninitialized || summariseState.isLoading,
  };
};

export const getOverrideCostDelta = ({
  overrideCost,
  replacedCost,
}: {
  overrideCost: CostSummary | undefined;
  replacedCost: CostSummary | undefined;
}): number | null =>
  overrideCost === undefined || replacedCost === undefined
    ? null
    : overrideCost.display_cost_per_tonne - replacedCost.display_cost_per_tonne;

export const useOverrideCostDelta = ({
  overrideMixId,
  replacedMixId,
}: {
  overrideMixId: number;
  replacedMixId: number;
}): { deltaPerTonne: number | null; isPending: boolean } => {
  const { costByMixId, isPending } = useOverrideCostSummaryMap();
  return {
    deltaPerTonne: getOverrideCostDelta({
      overrideCost: costByMixId.get(overrideMixId),
      replacedCost: costByMixId.get(replacedMixId),
    }),
    isPending,
  };
};
